import { useState } from 'react'
import { query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { AuditRow, Paged } from '../../api/types'
import { Empty, ErrorBanner, Loading, Modal, Panel, Tag, formatDateTime } from '../../components/ui'

/**
 * The audit trail. Written by an interceptor at the persistence layer, so nothing that changes a
 * row can avoid appearing here, and the rows themselves are append-only (ARCHITECTURE §4).
 */
export function AuditLogPage() {
  const [table, setTable] = useState('')
  const [userName, setUserName] = useState('')
  const [action, setAction] = useState('')
  const [page, setPage] = useState(1)
  const [inspecting, setInspecting] = useState<AuditRow | null>(null)

  const log = useQuery<Paged<AuditRow>>(
    `/admin/audit${query({ table, userName, action, page, size: 100 })}`,
    [table, userName, action, page],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Table</label>
          <input value={table} onChange={(e) => { setTable(e.target.value); setPage(1) }} placeholder="JournalEntry" />
        </div>

        <div className="field">
          <label>User</label>
          <input value={userName} onChange={(e) => { setUserName(e.target.value); setPage(1) }} />
        </div>

        <div className="field">
          <label>Action</label>
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1) }}>
            <option value="">All</option>
            <option value="Insert">Insert</option>
            <option value="Update">Update</option>
            <option value="Delete">Delete</option>
            <option value="Post">Post</option>
            <option value="Reverse">Reverse</option>
            <option value="PeriodLock">Period lock</option>
            <option value="PeriodReopen">Period reopen</option>
            <option value="Login">Login</option>
            <option value="LoginFailed">Failed login</option>
          </select>
        </div>

        <div className="spacer" />

        <span className="muted">{log.data?.total ?? 0} entries</span>
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          ← Previous
        </button>
        <button
          disabled={!log.data || page * log.data.size >= log.data.total}
          onClick={() => setPage((p) => p + 1)}
        >
          Next →
        </button>
      </div>

      <ErrorBanner error={log.error} />
      {log.loading && <Loading />}

      {log.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>When</th>
                  <th style={{ width: 110 }}>Action</th>
                  <th style={{ width: 150 }}>Table</th>
                  <th style={{ width: 170 }}>User</th>
                  <th>Detail</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {log.data.items.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <Empty>Nothing matches.</Empty>
                    </td>
                  </tr>
                )}

                {log.data.items.map((row) => (
                  <tr key={row.id}>
                    <td className="nowrap soft">{formatDateTime(row.timestamp)}</td>
                    <td>
                      <Tag
                        kind={
                          row.action === 'Delete' || row.action === 'LoginFailed' || row.action === 'PeriodReopen'
                            ? 'danger'
                            : row.action === 'Post' || row.action === 'Login'
                              ? 'ok'
                              : 'default'
                        }
                      >
                        {row.action}
                      </Tag>
                    </td>
                    <td className="code">{row.tableName}</td>
                    <td className="soft">{row.userName}</td>
                    <td className="soft">
                      {row.summary ?? row.changedColumns ?? <span className="muted">—</span>}
                    </td>
                    <td>
                      {(row.before || row.after) && (
                        <button className="subtle small" onClick={() => setInspecting(row)}>
                          Changes
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {inspecting && (
        <Modal title={`${inspecting.action} on ${inspecting.tableName}`} width="wide" onClose={() => setInspecting(null)}>
          <div className="muted" style={{ marginBottom: 12, fontSize: 12 }}>
            {formatDateTime(inspecting.timestamp)} · {inspecting.userName} · {inspecting.ipAddress}
            {inspecting.recordId && ` · record ${inspecting.recordId}`}
          </div>

          <div className="grid cols-2">
            <div>
              <label>Before</label>
              <pre style={{ background: 'var(--surface-sunken)', padding: 12, borderRadius: 6, overflow: 'auto', fontSize: 12 }}>
                {formatJson(inspecting.before)}
              </pre>
            </div>
            <div>
              <label>After</label>
              <pre style={{ background: 'var(--surface-sunken)', padding: 12, borderRadius: 6, overflow: 'auto', fontSize: 12 }}>
                {formatJson(inspecting.after)}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

function formatJson(raw: string | null | undefined): string {
  if (!raw) return '—'

  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
